import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { alaCarteMenu, pizzaMenu } from '../data/menuData';
import { useTranslation } from '../hooks/useTranslation';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, X, MapPin, Clock, Truck, ChevronRight, Star, Flame, Pizza, Gift, User, Award } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OrderPage = () => {
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('popular');
  const [deliveryMethod, setDeliveryMethod] = useState('pickup');
  const [customerInfo, setCustomerInfo] = useState({
    name: '', phone: '', email: '', address: '', specialInstructions: '', distance: 0
  });
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [submittedOrderId, setSubmittedOrderId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Loyalty state
  const [loggedInCustomer, setLoggedInCustomer] = useState(null);
  const [guestMode, setGuestMode] = useState(false);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginName, setLoginName] = useState('');
  const [loginReferralCode, setLoginReferralCode] = useState('');
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [rewards, setRewards] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);
  const [welcomeMsg, setWelcomeMsg] = useState('');
  
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Check if already logged in
  useEffect(() => {
    const saved = localStorage.getItem('sunsari_customer');
    if (saved) {
      try {
        const c = JSON.parse(saved);
        setLoggedInCustomer(c);
        setCustomerInfo(prev => ({ ...prev, name: c.name, phone: c.phone }));
        fetchRewards(c.id);
      } catch {}
    }
  }, []);

  const fetchRewards = async (customerId) => {
    try {
      const res = await axios.get(`${API}/api/customers/${customerId}/rewards`);
      setRewards(res.data.rewards || []);
      setLoggedInCustomer(prev => prev ? { ...prev, loyalty_points: res.data.points } : prev);
    } catch {}
  };

  // Consume pending items dropped in by "Add to Cart" on the homepage MenuSection
  useEffect(() => {
    try {
      const pending = JSON.parse(localStorage.getItem('sunsari_pending_cart') || '[]');
      if (pending.length > 0) {
        setCart(prev => {
          const next = [...prev];
          pending.forEach((p) => {
            const existing = next.find(i => i.name === p.name);
            if (existing) existing.quantity += 1;
            else next.push({ ...p, quantity: 1 });
          });
          return next;
        });
        localStorage.removeItem('sunsari_pending_cart');
        setShowCart(true);
      }
    } catch {}
  }, []);

  // Capture ?ref=CODE from URL on mount → prefill login referral code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setLoginReferralCode(ref.toUpperCase());
      setShowReferralInput(true);
      try { localStorage.setItem('sunsari_ref_code', ref.toUpperCase()); } catch {}
    } else {
      try {
        const cached = localStorage.getItem('sunsari_ref_code');
        if (cached) { setLoginReferralCode(cached); setShowReferralInput(true); }
      } catch {}
    }
  }, []);

  const handlePhoneLogin = async () => {
    if (!loginPhone.trim()) { setLoginError('Enter your phone number'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      const payload = { phone: loginPhone, name: loginName };
      if (loginReferralCode.trim()) payload.referral_code = loginReferralCode.trim().toUpperCase();
      const res = await axios.post(`${API}/api/customers/phone-login`, payload);
      const c = res.data.customer;
      setLoggedInCustomer(c);
      localStorage.setItem('sunsari_customer', JSON.stringify(c));
      setCustomerInfo(prev => ({ ...prev, name: c.name, phone: c.phone }));
      fetchRewards(c.id);
      if (res.data.isNew) {
        const referralMsg = res.data.referralApplied ? ' Your friend will earn 50 pts when you place your first order!' : '';
        setWelcomeMsg(`Welcome! You earned ${res.data.welcomeBonus} bonus points!${referralMsg}`);
        try { localStorage.removeItem('sunsari_ref_code'); } catch {}
      } else {
        setWelcomeMsg(`Welcome back! You have ${c.loyalty_points} points.`);
      }
      setGuestMode(false);
      setTimeout(() => setWelcomeMsg(''), 7000);
    } catch {
      setLoginError('Something went wrong. Try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('sunsari_customer');
    setLoggedInCustomer(null);
    setRewards([]);
    setSelectedReward(null);
    setGuestMode(false);
  };

  const proceedToCheckout = () => {
    setShowCart(false);
    setShowCheckout(true);
  };

  const skipLogin = () => {
    setLoginPhone('');
    setLoginName('');
    setLoginError('');
    setGuestMode(true);
  };

  // Offer of the week
  const offerItem = {
    name: "Chicken Tikka Masala",
    description: "Yogurt-marinated grilled chicken simmered in aromatic tikka masala sauce with fresh cream & spices",
    price: "18,50€",
    image: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=600&q=80",
    isOffer: true
  };

  const popularDishes = [
    ...(alaCarteMenu.chicken?.items?.slice(0, 2) || []),
    ...(alaCarteMenu.lamb?.items?.slice(0, 1) || []),
    ...(alaCarteMenu.vegetarian?.items?.slice(0, 1) || [])
  ];

  const addToCart = (item) => {
    const existingItem = cart.find(cartItem => cartItem.name === item.name);
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.name === item.name ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemName) => setCart(cart.filter(item => item.name !== itemName));

  const updateQuantity = (itemName, newQuantity) => {
    if (newQuantity === 0) removeFromCart(itemName);
    else setCart(cart.map(item => item.name === itemName ? { ...item, quantity: newQuantity } : item));
  };

  const getSubtotal = () => cart.reduce((total, item) => {
    const price = parseFloat(item.price.replace('€', '').replace(',', '.'));
    return total + (price * item.quantity);
  }, 0);

  const getDeliveryFee = () => {
    if (deliveryMethod === 'pickup') return 0;
    const subtotal = getSubtotal();
    if (subtotal >= 100) return 0;
    const distance = parseFloat(customerInfo.distance) || 0;
    if (distance <= 2) return 4;
    else if (distance <= 6) return 6;
    return 6;
  };

  const isMinimumOrderMet = () => getSubtotal() >= 16;

  const getRewardDiscount = () => {
    if (!selectedReward || selectedReward.type !== 'discount') return 0;
    return selectedReward.value || 0;
  };

  const getFinalTotal = () => {
    const total = getSubtotal() + getDeliveryFee() - getRewardDiscount();
    return Math.max(0, total);
  };

  const handleSubmitOrder = async () => {
    if (!customerInfo.name || !customerInfo.phone) {
      alert('Please provide your name and phone number');
      return;
    }
    if (deliveryMethod === 'delivery' && !customerInfo.address) {
      alert('Please provide delivery address');
      return;
    }
    setIsSubmitting(true);
    try {
      // Build items list — append free item reward as 0€ line if redeemed
      const items = cart.map(item => ({ name: item.name, price: item.price, quantity: item.quantity }));
      if (selectedReward && selectedReward.type === 'free_item') {
        items.push({ name: `🎁 ${selectedReward.value} (Reward)`, price: '0,00€', quantity: 1 });
      }

      const orderData = {
        customer: customerInfo,
        items,
        subtotal: getSubtotal().toFixed(2),
        deliveryFee: getDeliveryFee().toFixed(2),
        totalPrice: getFinalTotal().toFixed(2),
        deliveryMethod,
        paymentMethod: "Cash on Delivery",
        orderDate: new Date().toISOString().split('T')[0],
        customer_id: loggedInCustomer?.id || null,
        points_redeemed: selectedReward ? selectedReward.points_cost : 0,
      };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, orderData);
      if (response.status === 200) {
        setSubmittedOrderId(response.data.orderId);
        setOrderSubmitted(true);
        setCart([]);
        // Refresh points after order
        if (loggedInCustomer) {
          setTimeout(() => fetchRewards(loggedInCustomer.id), 800);
        }
        setSelectedReward(null);
      }
    } catch (error) {
      alert('Failed to place order. Please try again or call us at 040 377 9200');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.quantity, 0);

  // MenuItem component
  const MenuItem = ({ item }) => (
    <div className="bg-white rounded-xl border border-[#D9D9D6] hover:shadow-md hover:border-[#C97A2B]/30 transition-all overflow-hidden group" data-testid={`menu-item-${item.name.replace(/\s+/g, '-').toLowerCase()}`}>
      {item.image && (
        <div className="h-32 overflow-hidden">
          <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="text-[#2F2F2F] font-medium text-sm leading-tight mb-1">{item.name}</h4>
            {item.description && <p className="text-[#6B705C] text-xs leading-relaxed line-clamp-2">{item.description}</p>}
            {item.dietary && item.dietary.length > 0 && (
              <div className="flex gap-1 mt-1.5">
                {item.dietary.includes('vegetarian') && <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">V</span>}
                {item.dietary.includes('gluten-free') && <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">GF</span>}
                {item.dietary.includes('nut-free') && <span className="text-[9px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">NF</span>}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <span className="text-[#C97A2B] font-semibold text-sm">{item.price}</span>
            <button
              onClick={() => addToCart(item)}
              className="w-8 h-8 flex items-center justify-center bg-[#C97A2B] text-white rounded-full hover:bg-[#B65A3A] hover:scale-110 transition-all shadow-sm"
              data-testid={`add-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // All categories combined for tabs
  const allCategories = [
    { key: 'popular', label: 'Popular' },
    { key: 'pizza', label: 'Neapolitan Pizza' },
    ...Object.keys(alaCarteMenu).map(k => ({ key: k, label: alaCarteMenu[k].title }))
  ];

  // Pizza sub-categories
  const [pizzaSubCat, setPizzaSubCat] = useState('leRosse');

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      {/* Sticky Header Bar */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#D9D9D6] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-[#6B705C] hover:text-[#2F2F2F] transition-colors text-sm flex items-center gap-1" data-testid="back-btn">
              <ChevronRight className="w-4 h-4 rotate-180" /> Back
            </button>
            <div className="h-5 w-px bg-[#D9D9D6]" />
            <div>
              <h1 className="text-[#2F2F2F] text-lg font-semibold" style={{ fontFamily: 'DM Serif Display, serif' }}>Order Online</h1>
              <div className="flex items-center gap-1.5 text-xs text-[#6B705C]">
                <MapPin className="w-3 h-3" /> Hietasaarenkatu 18, Vaasa
              </div>
            </div>
          </div>

          {/* Delivery/Pickup Toggle */}
          <div className="flex items-center gap-2 bg-[#F5F3EF] rounded-full p-1">
            <button onClick={() => setDeliveryMethod('pickup')}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${deliveryMethod === 'pickup' ? 'bg-[#C97A2B] text-white shadow-sm' : 'text-[#6B705C] hover:text-[#2F2F2F]'}`}
              data-testid="pickup-btn">Pickup</button>
            <button onClick={() => setDeliveryMethod('delivery')}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${deliveryMethod === 'delivery' ? 'bg-[#C97A2B] text-white shadow-sm' : 'text-[#6B705C] hover:text-[#2F2F2F]'}`}
              data-testid="delivery-btn">Delivery</button>
          </div>

          {/* Cart Button */}
          <button onClick={() => setShowCart(!showCart)}
            className="relative flex items-center gap-2 bg-[#2F2F2F] text-white px-5 py-2.5 rounded-full hover:bg-[#C97A2B] transition-colors"
            data-testid="cart-btn">
            <ShoppingCart className="w-4 h-4" />
            <span className="text-sm font-medium">{getSubtotal().toFixed(2)}€</span>
            {cartTotal > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-[#B65A3A] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{cartTotal}</span>
            )}
          </button>
        </div>
      </div>

      {/* "Cheaper than Wolt" Banner */}
      <div className="bg-gradient-to-r from-[#2F2F2F] to-[#1A1A1A] border-b border-[#C97A2B]/30">
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-center">
          <Star className="w-4 h-4 text-[#C97A2B] flex-shrink-0" />
          <p className="text-white text-xs md:text-sm font-medium">
            Order directly from us — <span className="text-[#C97A2B] font-bold">cheaper & faster</span> than Wolt!
            <span className="hidden md:inline text-white/60 ml-2">Support local, skip the middleman.</span>
          </p>
          <Star className="w-4 h-4 text-[#C97A2B] flex-shrink-0" />
        </div>
      </div>

      {deliveryMethod === 'delivery' && (
        <div className="bg-[#F5F3EF] border-b border-[#D9D9D6]">
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-center gap-4 text-xs text-[#6B705C]">
            <span><Truck className="w-3.5 h-3.5 inline mr-1" /> 0-2km: 4€</span>
            <span className="text-[#D9D9D6]">|</span>
            <span>2-6km: 6€</span>
            <span className="text-[#D9D9D6]">|</span>
            <span className="text-[#C97A2B] font-medium">FREE over 100€</span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Order Success */}
        {orderSubmitted ? (
          <div className="max-w-lg mx-auto bg-white rounded-2xl p-10 text-center border border-[#D9D9D6] shadow-sm" data-testid="order-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl text-[#2F2F2F] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>Order Placed!</h2>
            <p className="text-[#6B705C] text-sm mb-6">We'll call you shortly to confirm. You can track your order status anytime.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {submittedOrderId && (
                <button onClick={() => window.location.href = `/order-tracking/${submittedOrderId}`}
                  className="px-8 py-3 bg-[#2F2F2F] text-white rounded-full font-medium hover:bg-[#C97A2B] transition-colors" data-testid="track-order-btn">
                  Track Order
                </button>
              )}
              <button onClick={() => window.location.href = '/order-lookup'}
                className="px-8 py-3 border border-[#D9D9D6] text-[#2F2F2F] rounded-full font-medium hover:border-[#C97A2B] hover:text-[#C97A2B] transition-colors" data-testid="lookup-orders-btn">
                Look Up Orders
              </button>
            </div>
          </div>
        ) : showCheckout ? (
          /* Checkout Form */
          <div className="max-w-lg mx-auto">
            <button onClick={() => setShowCheckout(false)} className="text-[#6B705C] text-sm mb-6 hover:text-[#C97A2B] transition-colors flex items-center gap-1" data-testid="back-to-menu-btn">
              <ChevronRight className="w-4 h-4 rotate-180" /> Back to menu
            </button>
            <div className="bg-white rounded-2xl border border-[#D9D9D6] overflow-hidden">
              <div className="p-6 border-b border-[#D9D9D6]">
                <h2 className="text-xl text-[#2F2F2F]" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  {deliveryMethod === 'pickup' ? 'Pickup Details' : 'Delivery Details'}
                </h2>
              </div>
              <div className="p-6">
              {/* Loyalty / Login Section */}
              {!loggedInCustomer && !guestMode && (
                <div className="bg-gradient-to-br from-[#2F2F2F] to-[#1A1A1A] rounded-xl p-5 mb-6 text-white relative overflow-hidden" data-testid="login-section">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#C97A2B]/10 rounded-full blur-2xl" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Gift className="w-4 h-4 text-[#C97A2B]" />
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#C97A2B] font-medium">Sunsari Rewards</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'DM Serif Display, serif' }}>
                      Earn points with every order
                    </h3>
                    <p className="text-xs text-white/60 mb-4">
                      Login with your phone to earn <span className="text-[#C97A2B] font-semibold">20 welcome points</span> + 10 points per order. Redeem for discounts & free items.
                    </p>
                    <div className="space-y-2.5">
                      <input
                        type="tel"
                        value={loginPhone}
                        onChange={(e) => setLoginPhone(e.target.value)}
                        placeholder="Phone number (e.g. +358...)"
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#C97A2B] transition-colors"
                        data-testid="login-phone-input"
                      />
                      <input
                        type="text"
                        value={loginName}
                        onChange={(e) => setLoginName(e.target.value)}
                        placeholder="Your name (optional, for new customers)"
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#C97A2B] transition-colors"
                        data-testid="login-name-input"
                      />
                      {loginError && <p className="text-[#FF8A65] text-xs">{loginError}</p>}

                      {/* Referral code toggle/input */}
                      {!showReferralInput ? (
                        <button
                          type="button"
                          onClick={() => setShowReferralInput(true)}
                          className="text-[11px] text-[#C97A2B] hover:text-[#B65A3A] underline-offset-2 hover:underline text-left"
                          data-testid="toggle-referral-input"
                        >
                          Have a referral code? Add it →
                        </button>
                      ) : (
                        <div className="relative">
                          <input
                            type="text"
                            value={loginReferralCode}
                            onChange={(e) => setLoginReferralCode(e.target.value.toUpperCase())}
                            placeholder="Referral code (e.g. AB12CD34)"
                            maxLength={12}
                            className="w-full px-4 py-2.5 bg-[#C97A2B]/10 border border-[#C97A2B]/30 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#C97A2B] transition-colors tracking-widest font-mono uppercase"
                            data-testid="login-referral-input"
                          />
                          <p className="text-[10px] text-[#C97A2B]/80 mt-1.5 flex items-center gap-1">
                            <Gift className="w-3 h-3" /> You'll get +30 bonus pts on your first order
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={handlePhoneLogin}
                          disabled={loginLoading || !loginPhone.trim()}
                          className="flex-1 py-2.5 bg-[#C97A2B] text-white text-sm font-semibold rounded-lg hover:bg-[#B65A3A] transition-colors disabled:opacity-50"
                          data-testid="login-submit-btn"
                        >
                          {loginLoading ? 'Logging in...' : 'Login & Earn Points'}
                        </button>
                        <button
                          onClick={skipLogin}
                          className="px-4 py-2.5 bg-transparent border border-white/20 text-white/70 text-sm rounded-lg hover:bg-white/5 transition-colors"
                          data-testid="skip-login-btn"
                        >
                          Continue as guest
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Logged-in: Points + Rewards Panel */}
              {loggedInCustomer && loggedInCustomer.id && (
                <div className="bg-gradient-to-br from-[#C97A2B]/5 to-[#B65A3A]/10 border border-[#C97A2B]/20 rounded-xl p-5 mb-6" data-testid="loyalty-panel">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-[#C97A2B] text-white flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#2F2F2F]" data-testid="logged-in-name">Hi, {loggedInCustomer.name || 'friend'}!</p>
                        <p className="text-xs text-[#6B705C]">{loggedInCustomer.phone}</p>
                      </div>
                    </div>
                    <button onClick={handleLogout} className="text-[10px] text-[#6B705C] hover:text-[#C97A2B] uppercase tracking-wider" data-testid="logout-btn">
                      Logout
                    </button>
                  </div>

                  {welcomeMsg && (
                    <div className="bg-[#C97A2B]/15 border border-[#C97A2B]/30 rounded-lg px-3 py-2 mb-3 text-xs text-[#2F2F2F]" data-testid="welcome-msg">
                      {welcomeMsg}
                    </div>
                  )}

                  <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2.5 mb-3">
                    <Award className="w-4 h-4 text-[#C97A2B]" />
                    <span className="text-xs text-[#6B705C]">Your balance:</span>
                    <span className="text-sm font-bold text-[#C97A2B] ml-auto" data-testid="points-balance">{loggedInCustomer.loyalty_points || 0} pts</span>
                  </div>

                  {rewards.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B705C] font-medium mb-2">Redeem your points</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setSelectedReward(null)}
                          className={`p-2.5 rounded-lg text-left text-xs transition-all border ${
                            !selectedReward ? 'bg-[#2F2F2F] text-white border-[#2F2F2F]' : 'bg-white text-[#6B705C] border-[#D9D9D6] hover:border-[#C97A2B]/40'
                          }`}
                          data-testid="reward-none"
                        >
                          <p className="font-semibold">No reward</p>
                          <p className="text-[10px] opacity-70">Save points for later</p>
                        </button>
                        {rewards.map((r) => (
                          <button
                            key={r.id}
                            onClick={() => r.available && setSelectedReward(r)}
                            disabled={!r.available}
                            className={`p-2.5 rounded-lg text-left text-xs transition-all border ${
                              selectedReward?.id === r.id
                                ? 'bg-[#C97A2B] text-white border-[#C97A2B]'
                                : r.available
                                  ? 'bg-white text-[#2F2F2F] border-[#D9D9D6] hover:border-[#C97A2B]/40'
                                  : 'bg-[#F5F3EF] text-[#6B705C]/50 border-[#D9D9D6] cursor-not-allowed'
                            }`}
                            data-testid={`reward-${r.id}`}
                          >
                            <p className="font-semibold leading-tight">{r.name}</p>
                            <p className="text-[10px] opacity-80 mt-0.5">{r.points_cost} pts {!r.available && '• Not enough'}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Guest mode banner */}
              {guestMode && !loggedInCustomer && (
                <div className="bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl px-4 py-3 mb-6 flex items-center justify-between" data-testid="guest-banner">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#6B705C]" />
                    <p className="text-xs text-[#6B705C]">Ordering as guest — <button onClick={() => setGuestMode(false)} className="text-[#C97A2B] font-semibold hover:underline" data-testid="back-to-login-btn">login to earn points</button></p>
                  </div>
                </div>
              )}

              {deliveryMethod === 'pickup' && (
                <div className="bg-[#F5F3EF] rounded-xl p-4 mb-6 flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#C97A2B] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#2F2F2F]">Pickup from</p>
                    <p className="text-xs text-[#6B705C]">Hietasaarenkatu 18, 65100 Vaasa</p>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B705C] mb-1.5">Name *</label>
                  <input type="text" value={customerInfo.name} onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                    className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] text-sm focus:outline-none focus:border-[#C97A2B] transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B705C] mb-1.5">Phone *</label>
                  <input type="tel" value={customerInfo.phone} onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] text-sm focus:outline-none focus:border-[#C97A2B] transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B705C] mb-1.5">Email</label>
                  <input type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                    className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] text-sm focus:outline-none focus:border-[#C97A2B] transition-colors" />
                </div>
                {deliveryMethod === 'delivery' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-[#6B705C] mb-1.5">Delivery Address *</label>
                      <input type="text" value={customerInfo.address} onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                        className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] text-sm focus:outline-none focus:border-[#C97A2B] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#6B705C] mb-1.5">Distance (km)</label>
                      <select value={customerInfo.distance} onChange={(e) => setCustomerInfo({...customerInfo, distance: e.target.value})}
                        className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] text-sm focus:outline-none focus:border-[#C97A2B]">
                        <option value="0">Select distance</option>
                        <option value="1">0-2 km (4€)</option>
                        <option value="3">2-6 km (6€)</option>
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs font-medium text-[#6B705C] mb-1.5">Special Instructions</label>
                  <textarea rows={2} value={customerInfo.specialInstructions} onChange={(e) => setCustomerInfo({...customerInfo, specialInstructions: e.target.value})}
                    className="w-full px-4 py-3 bg-[#F5F3EF] border border-[#D9D9D6] rounded-xl text-[#2F2F2F] text-sm focus:outline-none focus:border-[#C97A2B] transition-colors resize-none" />
                </div>
              </div>

              {/* Order Summary */}
              <div className="mt-6 pt-6 border-t border-[#D9D9D6] space-y-3">
                {cart.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-[#6B705C]">{item.name} x{item.quantity}</span>
                    <span className="text-[#2F2F2F] font-medium">{(parseFloat(item.price.replace('€','').replace(',','.')) * item.quantity).toFixed(2)}€</span>
                  </div>
                ))}
                {selectedReward && selectedReward.type === 'free_item' && (
                  <div className="flex justify-between text-sm" data-testid="free-item-line">
                    <span className="text-[#C97A2B] font-medium">🎁 {selectedReward.value} (Reward)</span>
                    <span className="text-[#C97A2B] font-medium">FREE</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-[#6B705C]">
                  <span>{deliveryMethod === 'pickup' ? 'Pickup' : 'Delivery'}</span>
                  <span>{getDeliveryFee() === 0 ? (deliveryMethod === 'pickup' ? 'Free' : 'FREE') : `${getDeliveryFee().toFixed(2)}€`}</span>
                </div>
                {selectedReward && selectedReward.type === 'discount' && (
                  <div className="flex justify-between text-sm text-[#C97A2B]" data-testid="reward-discount-line">
                    <span>Reward discount ({selectedReward.points_cost} pts)</span>
                    <span className="font-medium">-{getRewardDiscount().toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-[#2F2F2F] pt-3 border-t border-[#D9D9D6]">
                  <span>Total</span>
                  <span className="text-[#C97A2B]" data-testid="final-total">{getFinalTotal().toFixed(2)}€</span>
                </div>
                {loggedInCustomer && loggedInCustomer.id && (
                  <p className="text-[10px] text-[#C97A2B] flex items-center gap-1">
                    <Gift className="w-3 h-3" /> You'll earn 10 points with this order
                  </p>
                )}
                <p className="text-[10px] text-[#6B705C]">Payment: Cash / Card / MobilePay {deliveryMethod === 'delivery' ? 'on delivery' : 'at pickup'}</p>
              </div>

              <button onClick={handleSubmitOrder} disabled={isSubmitting || !isMinimumOrderMet()}
                className="w-full mt-6 py-4 bg-[#C97A2B] text-white rounded-xl font-semibold text-sm hover:bg-[#B65A3A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-order-btn">
                {isSubmitting ? 'Placing Order...' : 'Place Order'}
              </button>
              </div>
            </div>
          </div>
        ) : (
          /* Menu Display */
          <>
            {/* Offer of the Week */}
            <div className="mb-10" data-testid="offer-of-week">
              <div className="bg-gradient-to-r from-[#1A1A1A] to-[#2F2F2F] rounded-2xl overflow-hidden relative">
                <div className="absolute top-0 right-0 bg-[#B65A3A] text-white text-[10px] tracking-widest uppercase px-4 py-1.5 rounded-bl-xl font-bold">
                  Offer of the Week
                </div>
                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/3 h-48 md:h-auto overflow-hidden">
                    <img src={offerItem.image} alt={offerItem.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 p-8">
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className="w-5 h-5 text-[#C97A2B]" />
                      <span className="text-[#C97A2B] text-xs tracking-[0.2em] uppercase font-medium">Special Offer</span>
                    </div>
                    <h3 className="text-white text-2xl md:text-3xl mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                      {offerItem.name}
                    </h3>
                    <p className="text-white/60 text-sm mb-5 max-w-md">{offerItem.description}</p>
                    <div className="flex items-center gap-4">
                      <span className="text-[#C97A2B] text-3xl font-bold">{offerItem.price}</span>
                      <button onClick={() => addToCart(offerItem)}
                        className="px-6 py-2.5 bg-[#C97A2B] text-white rounded-full font-medium hover:bg-[#B65A3A] transition-colors flex items-center gap-2"
                        data-testid="add-offer-btn">
                        <Plus className="w-4 h-4" /> Add to Order
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex overflow-x-auto gap-2 mb-8 pb-2 scrollbar-hide" data-testid="category-tabs">
              {allCategories.map((cat) => (
                <button key={cat.key}
                  onClick={() => { setSelectedCategory(cat.key); setSearchQuery(''); }}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    selectedCategory === cat.key
                      ? 'bg-[#C97A2B] text-white shadow-md'
                      : 'bg-white text-[#6B705C] border border-[#D9D9D6] hover:border-[#C97A2B]/50'
                  }`}
                  data-testid={`cat-${cat.key}`}>
                  {cat.key === 'pizza' && <Pizza className="w-3.5 h-3.5" />}
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="max-w-md mb-8">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B705C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search dishes..." data-testid="search-input"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D9D9D6] rounded-full text-sm text-[#2F2F2F] placeholder-[#6B705C]/60 focus:outline-none focus:border-[#C97A2B] transition-colors" />
              </div>
            </div>

            {/* Menu Items */}
            {selectedCategory === 'popular' ? (
              <div>
                <h3 className="text-xl text-[#2F2F2F] mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>Most Popular</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {popularDishes.map((item, idx) => <MenuItem key={idx} item={item} />)}
                </div>
              </div>
            ) : selectedCategory === 'pizza' ? (
              /* Neapolitan Pizza Section */
              <div>
                <div className="text-center mb-8">
                  <p className="text-[#6B705C] text-xs tracking-[0.3em] uppercase mb-2">Artisan Wood-Fired</p>
                  <h3 className="text-3xl text-[#2F2F2F] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    Neapolitan <span className="text-[#C97A2B] italic">Pizza</span>
                  </h3>
                  <p className="text-[#6B705C] text-sm max-w-lg mx-auto">Authentic Neapolitan pizza baked in our wood-fired oven at 450°C. San Marzano tomatoes, Italian flour & fresh ingredients.</p>
                </div>

                {/* Pizza Sub-category Tabs */}
                <div className="flex overflow-x-auto gap-2 mb-8 pb-2 scrollbar-hide">
                  {Object.keys(pizzaMenu).map((key) => (
                    <button key={key}
                      onClick={() => setPizzaSubCat(key)}
                      className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        pizzaSubCat === key
                          ? 'bg-[#2F2F2F] text-white'
                          : 'bg-white text-[#6B705C] border border-[#D9D9D6] hover:border-[#C97A2B]/50'
                      }`}
                      data-testid={`pizza-cat-${key}`}>
                      {pizzaMenu[key].title}
                    </button>
                  ))}
                </div>

                {/* Pizza Items */}
                <div>
                  <h4 className="text-lg text-[#2F2F2F] mb-1 font-semibold">{pizzaMenu[pizzaSubCat].title}</h4>
                  {pizzaMenu[pizzaSubCat].subtitle && (
                    <p className="text-[#6B705C] text-xs mb-6 italic">{pizzaMenu[pizzaSubCat].subtitle}</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(searchQuery
                      ? pizzaMenu[pizzaSubCat].items.filter(item =>
                          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.description.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                      : pizzaMenu[pizzaSubCat].items
                    ).map((item, idx) => (
                      <div key={idx} className={`bg-white rounded-xl border ${item.isSpecial ? 'border-[#C97A2B] ring-1 ring-[#C97A2B]/20' : 'border-[#D9D9D6]'} hover:shadow-md transition-all p-4`}>
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            {item.isSpecial && <span className="text-[9px] font-bold text-[#C97A2B] bg-[#C97A2B]/10 px-2 py-0.5 rounded-full uppercase tracking-wider">Special</span>}
                            <h4 className="text-[#2F2F2F] font-medium text-sm leading-tight mb-1 mt-1">{item.name}</h4>
                            <p className="text-[#6B705C] text-xs leading-relaxed">{item.description}</p>
                            {item.dietary?.includes('vegetarian') && (
                              <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded mt-1.5 inline-block">V</span>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className="text-[#C97A2B] font-semibold text-sm">{item.price}</span>
                            <button onClick={() => addToCart(item)}
                              className="w-8 h-8 flex items-center justify-center bg-[#C97A2B] text-white rounded-full hover:bg-[#B65A3A] hover:scale-110 transition-all shadow-sm">
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-center text-[#6B705C] text-xs mt-8 italic">(V) = Saatavilla kasvisvaihtoehtona</p>
              </div>
            ) : (
              <div>
                <h3 className="text-xl text-[#2F2F2F] mb-1" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  {alaCarteMenu[selectedCategory].title}
                </h3>
                {alaCarteMenu[selectedCategory].subtitle && (
                  <p className="text-[#6B705C] text-sm mb-6">{alaCarteMenu[selectedCategory].subtitle}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(searchQuery
                    ? alaCarteMenu[selectedCategory].items.filter(item =>
                        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        item.description.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                    : alaCarteMenu[selectedCategory].items
                  ).map((item, idx) => <MenuItem key={idx} item={item} />)}
                </div>
              </div>
            )}

            {/* Info Footer Cards */}
            <div className="mt-12 grid md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 border border-[#D9D9D6] text-center">
                <Truck className="w-5 h-5 text-[#C97A2B] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#2F2F2F]">Min. Order 16€</p>
                <p className="text-xs text-[#6B705C]">For delivery & pickup</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-[#D9D9D6] text-center">
                <Clock className="w-5 h-5 text-[#C97A2B] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#2F2F2F]">Opening Hours</p>
                <p className="text-xs text-[#6B705C]">Mon-Thu: 11:00-21:00</p>
                <p className="text-xs text-[#6B705C]">Fri: 11:00-22:00</p>
                <p className="text-xs text-[#6B705C]">Sat: 12:00-22:00</p>
                <p className="text-xs text-[#6B705C]">Sun: 12:00-20:00</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-[#D9D9D6] text-center">
                <MapPin className="w-5 h-5 text-[#C97A2B] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#2F2F2F]">Hietasaarenkatu 18</p>
                <p className="text-xs text-[#6B705C]">65100 Vaasa</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <>
          <div className="fixed inset-0 bg-black/30 z-[95]" onClick={() => setShowCart(false)} />
          <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl z-[100] flex flex-col" data-testid="cart-sidebar">
            <div className="flex items-center justify-between p-5 border-b border-[#D9D9D6]">
              <h3 className="text-[#2F2F2F] font-semibold" style={{ fontFamily: 'DM Serif Display, serif' }}>Your Order</h3>
              <button onClick={() => setShowCart(false)} className="text-[#6B705C] hover:text-[#2F2F2F]"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-10 h-10 text-[#D9D9D6] mx-auto mb-3" />
                  <p className="text-[#6B705C] text-sm">Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 pb-4 border-b border-[#D9D9D6]/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-[#2F2F2F] text-sm font-medium truncate">{item.name}</p>
                        <p className="text-[#C97A2B] text-xs font-semibold">{item.price}</p>
                      </div>
                      <div className="flex items-center gap-2 bg-[#F5F3EF] rounded-full px-2 py-1">
                        <button onClick={() => updateQuantity(item.name, item.quantity - 1)} className="w-6 h-6 flex items-center justify-center text-[#6B705C] hover:text-[#2F2F2F]">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-[#2F2F2F] text-sm font-medium w-4 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.name, item.quantity + 1)} className="w-6 h-6 flex items-center justify-center text-[#6B705C] hover:text-[#2F2F2F]">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-5 border-t border-[#D9D9D6] space-y-3 bg-[#F5F3EF]">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B705C]">Subtotal</span>
                  <span className="text-[#2F2F2F] font-medium">{getSubtotal().toFixed(2)}€</span>
                </div>
                {deliveryMethod === 'delivery' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B705C]">Delivery</span>
                    <span className="text-[#2F2F2F] font-medium">{getDeliveryFee().toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold text-[#2F2F2F] pt-2 border-t border-[#D9D9D6]">
                  <span>Total</span>
                  <span className="text-[#C97A2B]">{(getSubtotal() + getDeliveryFee()).toFixed(2)}€</span>
                </div>
                {!isMinimumOrderMet() && (
                  <p className="text-xs text-[#B65A3A]">Minimum order is 16€ (add {(16 - getSubtotal()).toFixed(2)}€ more)</p>
                )}
                <button onClick={proceedToCheckout} disabled={!isMinimumOrderMet()}
                  className="w-full py-3.5 bg-[#C97A2B] text-white rounded-full font-semibold text-sm hover:bg-[#B65A3A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="checkout-btn">
                  Checkout
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OrderPage;
